/**
 * Language Switcher Component
 * Allows users to switch between available languages
 */

import React from 'react';
import { useTranslation } from '../contexts/TranslationContext';
import { Languages } from 'lucide-react';
import Button from './ui/Button';

interface LanguageSwitcherProps {
  variant?: 'default' | 'outline' | 'ghost';
  size?: 'sm' | 'md' | 'lg';
  showLabel?: boolean;
}

const LanguageSwitcher: React.FC<LanguageSwitcherProps> = ({
  variant = 'ghost',
  size = 'sm',
  showLabel = false,
}) => {
  const { locale, changeLocale, availableLocales } = useTranslation();
  const currentLocale = availableLocales.find(l => l.code === locale);

  const handleLanguageChange = async (newLocale: string) => {
    if (newLocale !== locale) {
      await changeLocale(newLocale);
    }
  };

  return (
    <div className="relative group">
      <Button
        variant={variant}
        size={size}
        className="flex items-center gap-2"
      >
        <Languages className="w-4 h-4" />
        {showLabel && currentLocale && (
          <span>{currentLocale.nativeName}</span>
        )}
        {!showLabel && currentLocale && (
          <span className="text-lg leading-none" style={{ fontFamily: 'system-ui, -apple-system' }}>{currentLocale.flag}</span>
        )}
      </Button>
      
      <div className="absolute right-0 mt-2 w-48 bg-card border border-border rounded-lg shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
        <div className="p-2">
          {availableLocales.map((lang) => (
            <button
              key={lang.code}
              onClick={() => handleLanguageChange(lang.code)}
              className={`w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors ${
                locale === lang.code
                  ? 'bg-primary text-primary-foreground'
                  : 'text-foreground hover:bg-muted'
              }`}
            >
              <span className="text-lg leading-none" style={{ fontFamily: 'system-ui, -apple-system' }}>{lang.flag}</span>
              <div className="flex-1 text-left">
                <div className="font-medium">{lang.nativeName}</div>
                <div className="text-xs opacity-75">{lang.name}</div>
              </div>
              {locale === lang.code && (
                <span className="text-xs">✓</span>
              )}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default LanguageSwitcher;

